
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { createPieChartData, percentageFormatter } from './SwingChartUtils';
import { DistanceData } from './types';

interface CombinedDistancePieChartProps {
  title: string;
  distanceData: DistanceData;
  loading: boolean;
  borderColor: string;
}

const CombinedDistancePieChart: React.FC<CombinedDistancePieChartProps> = ({
  title,
  distanceData,
  loading,
  borderColor
}) => {
  const { t, i18n } = useTranslation('common');
  const pieData = createPieChartData(distanceData, i18n.language);
  
  // If no data, show empty state
  if (distanceData.total === 0 && !loading) {
    return (
      <Card className="shadow-md bg-white border border-gray-100 hover:shadow-lg transition-shadow">
        <CardContent className="p-5 text-center">
          <h4 className="font-medium text-lg mb-2 px-1 border-l-4 pl-3" style={{ borderColor: borderColor.replace('border-', '') }}>{title}</h4>
          <div className="text-gray-500 text-sm">{t('swingTendencies.noData')}</div>
        </CardContent>
      </Card>
    );
  }
  
  // If loading, show loader
  if (loading) {
    return (
      <Card className="shadow-md bg-white border border-gray-100">
        <CardContent className="p-5 flex flex-col items-center justify-center min-h-[240px]">
          <Loader2 className="h-8 w-8 text-green-600 animate-spin mb-3" />
          <h4 className="font-medium">{t('swingTendencies.loading')}</h4>
        </CardContent>
      </Card>
    );
  }
  
  // Calculate percentage
  const getPercentage = (value: number) => {
    return distanceData.total > 0 
      ? Math.round((value / distanceData.total) * 100) 
      : 0;
  };
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-lg">
          <p className="font-medium text-gray-800">{data.name}</p>
          <p className="text-gray-600">{data.value} {t('swingTendencies.shots')} ({getPercentage(data.value)}%)</p>
        </div>
      );
    }
    return null;
  };

  // Custom Legend to remove the box around legend items
  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex justify-center space-x-4 text-sm mt-2">
        {payload.map((entry: any, index: number) => (
          <div key={`legend-${index}`} className="flex items-center">
            <div 
              className="w-3 h-3 rounded-full mr-1" 
              style={{ backgroundColor: entry.color }}
            />
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <Card className="shadow-md bg-white border border-gray-100 hover:shadow-lg transition-shadow">
      <CardContent className="p-5">
        <h4 className={`font-semibold text-lg text-gray-800 px-1 border-l-4 pl-3 mb-4 ${borderColor}`}>{title}</h4>
        
        {distanceData.total > 0 && (
          <p className="text-sm text-gray-600 mb-2">{distanceData.total} {t('swingTendencies.shots')}</p>
        )}
        
        <div className="h-[220px] w-full mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                strokeWidth={2}
                stroke="#ffffff"
              >
                {pieData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color} 
                    className="drop-shadow-sm"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        <div className="grid grid-cols-3 gap-2 mt-4 text-sm bg-gray-50 rounded-lg p-3">
          <div className="text-center">
            <span className="font-medium text-blue-500 block mb-1">{t('swingTendencies.directions.left')}</span> 
            <div>{distanceData.left} ({getPercentage(distanceData.left)}%)</div>
          </div>
          <div className="text-center border-x border-gray-200">
            <span className="font-medium text-green-500 block mb-1">{t('swingTendencies.directions.center')}</span> 
            <div>{distanceData.center} ({getPercentage(distanceData.center)}%)</div>
          </div>
          <div className="text-center">
            <span className="font-medium text-red-500 block mb-1">{t('swingTendencies.directions.right')}</span> 
            <div>{distanceData.right} ({getPercentage(distanceData.right)}%)</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CombinedDistancePieChart;
